import React, { useEffect, useState } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { ICourseSummary, ICourseWaitlist } from "@eagerdog/interfaces";
import { AxiosError } from "axios";
import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { toast } from "src/components/Toast/ToastManager";
import { apiService } from "src/services/api.service";

import { CourseWaitlistActions } from "./CourseWaitlistActions";
import styles from "./CourseWaitlistTab.module.scss";

const STATUS_LABELS: Record<string, string> = {
  [Constants.waitlist_status.waitlist]: "Waitlisted",
  [Constants.waitlist_status.awaiting_payment]: "Awaiting Payment",
  [Constants.waitlist_status.canceled]: "Canceled",
  [Constants.waitlist_status.declined]: "Declined",
  [Constants.waitlist_status.registered]: "Registered",
};

interface IProps {
  course: ICourseSummary;
}

interface IRow {
  call_name: string;
  session: string;
  position: string;
  status: string;
  note: string;
  handler_name: string;
  handler_email: string;
  joined: string;
  entry: ICourseWaitlist;
  canApprove: boolean;
}

const CourseWaitlistTab: React.FC<IProps> = ({ course }) => {
  const [rows, setRows] = useState<ICourseWaitlist[]>([]);
  const [loading, setLoading] = useState(true);
  const isClass = course.course_type === Constants.course_type.drop_in;
  const isSeminar = course.course_type === Constants.course_type.seminar;

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
  });

  const load = () => {
    setLoading(true);
    apiService.searchCourseWaitlist(course._id).then((response) => {
      setRows(response || []);
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Waitlist",
        content: "Could not load the waitlist",
        duration: 8000,
        errorDetails: error,
        type: "fail",
      });
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  // course id is stable for this page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course._id]);

  const sessionLabel = (row: ICourseWaitlist) => {
    const index = (course.schedule || []).findIndex((session) => String(session._id) === String(row.session_id));
    return index >= 0 ? `Session ${index + 1}` : "Session";
  };

  const roomFor = (row: ICourseWaitlist) => {
    if (isClass) {
      const session = (course.schedule || []).find((item) => String(item._id) === String(row.session_id));
      if (!session || typeof session.regular_capacity !== "number") return true;
      const enrolled = session.enrolled_count ?? session.regular_entries ?? 0;
      return enrolled < session.regular_capacity;
    }
    if (!course.has_entry_limit) return true;
    return (course.enrolled_count || 0) < (course.entry_limit || 0);
  };

  const colDefs: ColDef<IRow>[] = [
    {
      field: "call_name",
      headerName: isSeminar ? "Name" : "Call Name",
      minWidth: 140,
      filter: "agTextColumnFilter",
    },
  ];

  if (isClass) {
    colDefs.push({
      field: "session",
      minWidth: 120,
      cellDataType: "text",
      filter: "agTextColumnFilter",
    });
  }

  colDefs.push(
    {
      field: "position",
      headerName: "Position",
      minWidth: 110,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      filter: "agTextColumnFilter",
    },
    {
      field: "note",
      headerName: "Note",
      minWidth: 220,
      filter: "agTextColumnFilter",
    },
    {
      field: "handler_name",
      headerName: "Handler's name",
      minWidth: 140,
      filter: "agTextColumnFilter",
    },
    {
      field: "handler_email",
      headerName: "Handler's email",
      minWidth: 180,
      filter: "agTextColumnFilter",
    },
    {
      field: "joined",
      headerName: "Joined",
      minWidth: 160,
    },
    {
      headerName: "",
      colId: "actions",
      cellRenderer: CourseWaitlistActions,
      cellRendererParams: {
        courseId: course._id,
        onChanged: load,
      },
      sortable: false,
      filter: false,
      resizable: false,
      maxWidth: 64,
      minWidth: 64,
      cellStyle: {
        overflow: "visible",
        zIndex: "1",
      },
    },
  );

  const rowData: IRow[] = rows.map((row) => {
    const open = roomFor(row);
    const waiting = row.status === Constants.waitlist_status.waitlist;
    return {
      call_name: isSeminar
        ? (row.owner?.display_name || "")
        : (row.dog?.call_name || row.owner?.display_name || ""),
      session: sessionLabel(row),
      position: waiting ? String(row.position) : "",
      status: STATUS_LABELS[row.status] || row.status,
      note: waiting && !open ? "The list is at its entry limit." : "",
      handler_name: row.owner?.display_name || "",
      handler_email: row.owner?.email || "",
      joined: row.created_at ? moment(row.created_at).format("MMMM Do, YY, hh:mmA") : "",
      entry: row,
      canApprove: open,
    };
  });

  return (
    <div className={styles.CourseWaitlist}>
      <AgGridReact
        theme={theme}
        rowData={rowData}
        columnDefs={colDefs}
        domLayout="normal"
        gridOptions={{
          defaultColDef: {
            flex: 1,
            minWidth: 100,
            resizable: true,
          },
          animateRows: true,
          rowHeight: 48,
          headerHeight: 48,
          overlayNoRowsTemplate: loading
            ? "<span>Loading waitlist…</span>"
            : "<span>No one is on the waitlist.</span>",
        }}
      />
    </div>
  );
};

export default CourseWaitlistTab;
